/**
 * How a report picks its units, in plain words, and the customers and regions a
 * run can be narrowed to. Shared by the report page and the workflow report step.
 */
import { z } from 'zod';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';

export interface ReportRosterWords {
  kind: 'filters' | 'unit_list';
  billingStatus: string | null;
  fleetStatus: string | null;
  unitComponent: string | null;
}

const fixedWords = (units: ReportRosterWords): string[] =>
  [
    units.billingStatus ? `billing status ${units.billingStatus}` : null,
    units.fleetStatus ? `fleet status ${units.fleetStatus}` : null,
    units.unitComponent ? `build ${units.unitComponent}` : null,
  ].filter((w): w is string => w !== null);

/** "Covers every unit with billing status Standby." */
export const coversLine = (units: ReportRosterWords): string => {
  if (units.kind === 'unit_list') return 'Each run names the units it covers.';
  const fixed = fixedWords(units);
  return fixed.length
    ? `Covers every unit with ${fixed.join(' and ')}.`
    : 'Covers the customer or region each run picks.';
};

/** Which units one run covered: "Flywheel, OKC East", or the report's own filters. */
export const runUnitsLabel = (
  units: ReportRosterWords | undefined,
  values: { customer?: string | null; region?: string | null; units: string },
): string => {
  if (units?.kind === 'unit_list') return values.units || 'No units';
  const narrowed = [values.customer, values.region].filter((v): v is string => Boolean(v));
  if (narrowed.length) return narrowed.join(', ');
  const fixed = units ? fixedWords(units) : [];
  return fixed.length ? `All units with ${fixed.join(' and ')}` : 'All units';
};

const customersSchema = z.object({
  customers: z.array(z.object({ id: z.string(), name: z.string().nullable() }).passthrough()),
});
const regionsSchema = z.object({
  regions: z.array(z.object({ id: z.string(), name: z.string() }).passthrough()),
});

/** One option per customer name: a run names its customer, and a family shares one name. */
export const customersQuery = {
  queryKey: queryKeys.customers(),
  queryFn: async () => {
    const { customers } = customersSchema.parse(await apiRequest<unknown>('/api/customers'));
    const names = [...new Set(customers.flatMap((c) => (c.name ? [c.name] : [])))];
    return names.sort((a, b) => a.localeCompare(b)).map((name) => ({ value: name, label: name }));
  },
};

export const regionsQuery = {
  queryKey: queryKeys.regions(),
  queryFn: async () =>
    regionsSchema
      .parse(await apiRequest<unknown>('/api/regions'))
      .regions.map((r) => ({ value: r.name, label: r.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
};
